import {
  deserializeInterval,
  intervalsAreEqual,
  serializeInterval,
} from "./intervals";
import { safeName } from "./recordTree";
import { HGAnyRecord, HGAnyRecordSerialized, HGRecord } from "./types";

export function serializeRecord(
  record: HGAnyRecord | null,
): HGAnyRecordSerialized | null {
  if (!record) return null;

  if ("interval" in record) {
    return Object.assign(
      { ...record },
      {
        start_second: record.start_second.toString(),
        end_second: record.end_second && record.end_second.toString(),
        interval: serializeInterval(record.interval),
        end_interval:
          record.end_interval && serializeInterval(record.end_interval),
      },
    );
  } else {
    return { ...record };
  }
}
export function deserializeRecord(
  record: HGAnyRecordSerialized | null,
): HGAnyRecord | null {
  if (!record) return null;

  if ("interval" in record) {
    return Object.assign(
      { ...record },
      {
        start_second: BigInt(record.start_second),
        end_second: record.end_second && BigInt(record.end_second),
        interval: deserializeInterval(record.interval),
        end_interval:
          record.end_interval && deserializeInterval(record.end_interval),
      },
    );
  } else {
    return { ...record };
  }
}

/**
 * The `intervalName` / `endIntervalName` pair that record views render.
 *
 * `enrichRecords` sets these on everything coming out of `/records/by-ival`, but
 * a record fetched on its own — the record page, or the title record embedded in
 * a collection — arrives without them. Same rule in both places: the end name is
 * only set when the end interval is genuinely a different interval, so a record
 * that starts and ends in 1750 reads "1750" and not "1750 - 1750".
 */
export function intervalNames(record: HGAnyRecord): {
  intervalName: string;
  endIntervalName: string | null;
} {
  if (!("interval" in record) || !record.interval) {
    return { intervalName: "", endIntervalName: null };
  }
  const endInterval = record.end_interval;
  return {
    intervalName: safeName(record.interval),
    endIntervalName:
      endInterval && !intervalsAreEqual(record.interval, endInterval)
        ? safeName(endInterval)
        : null,
  };
}

/**
 * Case-insensitive substring match over a record's user-visible text: title,
 * content, and any link labels/URLs. `query` must already be trimmed and
 * lowercased by the caller — matching against a whole result set is hot
 * enough that it isn't worth redoing that per record.
 */
export function recordMatches(record: HGRecord, query: string): boolean {
  if (record.title.toLowerCase().includes(query)) return true;
  if (record.content.toLowerCase().includes(query)) return true;
  return (record.url ?? []).some(
    (u) =>
      u.label.toLowerCase().includes(query) ||
      u.url.toLowerCase().includes(query),
  );
}
